// SceneryDebug — the `window.lfSceneryDebug` deep-inspection hook for the procedural-scenery
// render path (Set13 P5 / Set15 P4/P7). The SceneryRenderer pushes its LIVE render state here
// each `useFrame` body via `sceneryDebugUpdate`; an external tool (e.g. the agent browser
// session) reads a snapshot via `window.lfSceneryDebug.refresh()`.
//
// The goal: make the three.js internals the renderer holds (the R3F `scene`/`camera`/`gl`, the
// per-kind `InstancedMesh` objects + their instance counts) inspectable from the console without
// having to reach into React/R3F internals. Mirrors the `TerrainDebug3D` hook in
// `Terrain/Heights/TerrainDebug3D.ts` (which this file was patterned on).

/** The per-kind render state the renderer holds for one kind (duplicated structurally here so
 *  this module doesn't need to import the renderer — the renderer passes us a ready-to-serialize
 *  summary instead). */
export interface SceneryDebugKindState {
	kindID: string;
	isPerspective: boolean;
	/** True if the kind has a 3D far-tier billboard mesh. */
	hasBillboard: boolean;
	/** True if the kind has Set15 P7 near-tier real meshes. */
	hasNearMeshes: boolean;
	/** Billboard `InstancedMesh.count` (how many instances the far tier actually draws). */
	billboardCount: number;
	/** Near-tier total instance count across all variant bark/leaf meshes. */
	nearCount: number;
	/** 2D tree pass count (only populated in 2D). */
	treeCount: number;
	/** 2D shadow pass count (only populated in 2D). */
	shadowCount: number;
	/** Number of cached placement tiles for this kind. */
	tileCount: number;
	/** Atlas width/height (px). */
	atlasWidth: number;
	atlasHeight: number;
	/** The bake signature (pitch, light rig, resolved params — changes force a re-bake). */
	bakeSignature: string;
}

/** Live render state the hook snapshots. Re-pushed every `useFrame` body. */
export interface SceneryDebugState {
	/** Is the current camera perspective (3D)? */
	isPerspective: boolean;
	/** The eye/camera position. */
	eye: {x: number; y: number; z: number}|null;
	/** `sceneryMaxDistance` (3D draw distance; 0 in 2D). */
	sceneryMaxDistance: number;
	/** Per-frame mpp (metres/pixel) at viewport centre. */
	mpp: number;
	/** The visible world rect used for tile enumeration (vp in renderKind). */
	vp: {minX: number; minY: number; maxX: number; maxY: number}|null;
	/** The zoom-bucketed tile size (metres) for the current mpp. */
	tileSize: number;
	/** Number of scenery regions collected this render. */
	regionCount: number;
	/** Active kind ids (from the collected regions). */
	activeKindIDs: string[];
	/** Per-kind render state summary. */
	kinds: SceneryDebugKindState[];
	/** The active bake pitch (degrees below horizontal; 90 = top-down 2D). */
	bakePitch: number;
	/** Whether texture preload is ready (the bake gate). */
	texturesReady: boolean;
	/** Whether a rebuild is in flight (on-demand render keeps running while true). */
	needsRebuild: boolean;
	/** Option A diagnostics — the measured previous render() duration (ms). */
	lastRenderMs: number;
	/** Option A diagnostics — the resulting adaptive placement-chunk budget (ms), clamped to 3 s. */
	placementChunkMs: number;
	/** Option A diagnostics — how many tiles were actually placed in the most recent frame. */
	tilesPlacedThisFrame: number;
	/** Option A diagnostics — how much wall-clock (ms) the most recent frame's placement consumed. */
	placementMsThisFrame: number;
	/** The R3F scene (for live object inspection — meshes, lights). */
	scene: unknown;
	/** The R3F camera. */
	camera: unknown;
	/** The WebGL renderer. */
	gl: unknown;
	/** The live per-kind render state Map (keyed by kindID) — the ACTUAL objects, not just the
	 *  summary. Exposed for deep inspection (instance matrices, materials, geometry). */
	renderStateMap: unknown;
	/** The budget tracker (atlas bytes, per-frame instance allowance). */
	budgetTracker: unknown;
}

let state: SceneryDebugState = {
	isPerspective: false,
	eye: null,
	sceneryMaxDistance: 0,
	mpp: 1,
	vp: null,
	tileSize: 0,
	regionCount: 0,
	activeKindIDs: [],
	kinds: [],
	bakePitch: 90,
	texturesReady: false,
	needsRebuild: false,
	lastRenderMs: 0,
	placementChunkMs: 0,
	tilesPlacedThisFrame: 0,
	placementMsThisFrame: 0,
	scene: null,
	camera: null,
	gl: null,
	renderStateMap: null,
	budgetTracker: null,
};

/** Re-point the snapshot source to the LIVE renderer state (call each `useFrame` body). */
export function sceneryDebugUpdate(s: Partial<SceneryDebugState>): void {
	Object.assign(state, s);
}

/** Fresh snapshot of the live scenery render state. */
export function sceneryDebugRefresh(): any {
	const out: any = {
		isPerspective: state.isPerspective,
		eye: state.eye,
		sceneryMaxDistance: state.sceneryMaxDistance,
		mpp: state.mpp,
		vp: state.vp,
		tileSize: state.tileSize,
		regionCount: state.regionCount,
		activeKindIDs: state.activeKindIDs,
		kinds: state.kinds,
		bakePitch: state.bakePitch,
		texturesReady: state.texturesReady,
		needsRebuild: state.needsRebuild,
		// Option A diagnostics.
		lastRenderMs: state.lastRenderMs,
		placementChunkMs: state.placementChunkMs,
		tilesPlacedThisFrame: state.tilesPlacedThisFrame,
		placementMsThisFrame: state.placementMsThisFrame,
	};

	// The LIVE objects (non-serializable) — handed through so the console can inspect the actual
	// scene / camera / renderer / per-kind render-state Map / budget tracker directly.
	out.renderStateMap = state.renderStateMap;
	out.scene = state.scene;
	out.cameraLive = state.camera;
	out.gl = state.gl;
	out.budgetTracker = state.budgetTracker;

	// Camera summary.
	if (state.camera) {
		const c = state.camera as any;
		out.camera = {
			type: c.isPerspectiveCamera ? "perspective" : "ortho",
			position: {x: c.position.x, y: c.position.y, z: c.position.z},
			up: {x: c.up.x, y: c.up.y, z: c.up.z},
			near: c.near, far: c.far,
			fov: c.fov, zoom: c.zoom,
		};
	}

	// Scene graph walk (immediate + nested meshes/groups/lights).
	if (state.scene) {
		const s = state.scene as any;
		const children: any[] = [];
		const walk = (node: any, depth: number): void=>{
			if (depth > 3) return;
			if (node.isMesh) {
				children.push({
					type: "mesh",
					name: node.name,
					visible: node.visible,
					position: {x: node.position.x, y: node.position.y, z: node.position.z},
					scale: {x: node.scale.x, y: node.scale.y, z: node.scale.z},
					materialType: node.material?.type,
					// InstancedMesh-specific: how many instances are actually drawn.
					instanceCount: node.count ?? null,
					frustumCulled: node.frustumCulled,
					renderOrder: node.renderOrder,
				});
				return;
			}
			if (node.isLight) {
				children.push({
					type: "light",
					name: node.name,
					intensity: node.intensity,
					color: node.color?.getHex?.(),
				});
				return;
			}
			if (node.isGroup || node.isObject3D) {
				for (const ch of (node.children ?? [])) walk(ch, depth + 1);
			}
		};
		for (const ch of (s.children ?? [])) walk(ch, 0);
		out.sceneChildren = children;
		out.sceneMeshCount = children.filter((c: any)=>c.type == "mesh").length;
		out.sceneLightCount = children.filter((c: any)=>c.type == "light").length;
	}

	// Live per-kind render state (the ACTUAL objects — matrices, materials, geometry).
	if (state.renderStateMap) {
		const map = state.renderStateMap as Map<string, any>;
		const live: any = {};
		for (const [kindID, st] of map) {
			const bill = st.billboardMesh;
			const nearMeshes = st.nearMeshes;
			live[kindID] = {
				isPerspective: st.isPerspective,
				billboard: bill ? {
					visible: bill.visible,
					count: bill.count,
					instanceMatrixLen: bill.instanceMatrix?.array?.length ?? 0,
				} : null,
				nearCount: nearMeshes ? nearMeshes.reduce((a: number, nv: any)=>a + (nv.bark?.count ?? 0), 0) : 0,
				tileCacheSize: st.tileCache?.size ?? 0,
				bakeSignature: st.bakeSignature,
				atlasTextureSize: st.atlasTexture ? {w: st.atlasTexture.image?.width, h: st.atlasTexture.image?.height} : null,
			};
		}
		out.renderStateLive = live;
	}

	// Budget tracker summary.
	if (state.budgetTracker) {
		const bt = state.budgetTracker as any;
		out.budget = {
			instancesRemaining: bt.instancesRemaining,
			atlasBytesReserved: bt.atlasBytesReserved,
			maxAtlasBytes: bt.config?.maxAtlasBytes,
			maxInstancesPerFrame: bt.config?.maxInstancesPerFrame,
			placementRenderRatio: bt.config?.placementRenderRatio,
			nearMeshMaxDistance: bt.config?.nearMeshMaxDistance,
			nearMeshMaxInstances: bt.config?.nearMeshMaxInstances,
		};
	}

	return out;
}

declare global {
	interface Window {
		/** Scenery deep-inspection hook — snapshot of the live scenery render state.
		 *  Call `window.lfSceneryDebug.refresh()` for a fresh read. */
		lfSceneryDebug?: {refresh(): any};
	}
}

// Install at module load; re-installed on hot-reload (the renderer re-pushes state each body).
if (typeof window !== "undefined") {
	window.lfSceneryDebug = {refresh: sceneryDebugRefresh};
}
